import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Input,
  Modal,
  Select,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Statistic,
  message,
  ConfigProvider
} from 'antd';
import {
  FilterOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/vi';
import locale from 'antd/locale/vi_VN';

// Cấu hình timezone cho dayjs
dayjs.extend(utc);
dayjs.extend(timezone);
import { stockService } from '../../services/stockService';
import styles from './StockOutPage.module.css';
import { StockOutPrintModal } from '../../components/stock-out';
const { Search } = Input;
const { Option } = Select;


interface StockOutItem {
  id: number;
  medicine_id: number;
  medicine_name: string;
  medicine_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_id?: number | null;
  lot_number?: string;
  expiry_date?: string;
  total_amount: number;
  notes?: string;
  available_stock?: number;
}

interface StockOutRecord {
  id: number;
  reference_number?: string;
  voucher_number?: string;
  customer_name?: string;
  customer_phone?: string;
  customer_address?: string;
  total_items?: number;
  total_amount?: number;
  status?: 'pending' | 'completed' | 'cancelled';
  created_at?: string;
  completed_at?: string;
  created_by?: string;
  created_by_name?: string;
  notes?: string;
  items?: StockOutItem[];
  // For API detail response structure
  voucher?: {
    id: number;
    voucher_number: string;
    customer_name: string;
    customer_phone: string;
    customer_address: string;
    total_amount: number;
    notes: string;
    created_by: string;
    created_at: string;
    status: string;
  };
}



const StockOutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    customer: '',
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().endOf('month').format('YYYY-MM-DD')
  } as {
    status: string;
    customer: string;
    date_from: string | undefined;
    date_to: string | undefined;
  });
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printingRecordId, setPrintingRecordId] = useState<number | null>(null);

  // State cho phân trang
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const queryClient = useQueryClient();

  // Fetch data
  const { data: stockOutRecords = [], isLoading } = useQuery(
    ['stock-out', searchText, filters],
    () => stockService.getStockOutVouchers({
        search: searchText,
        status: filters.status,
        customer: filters.customer,
        start_date: filters.date_from,
        end_date: filters.date_to
    }),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnMount: true, // Tự động refetch khi mount lại trang
      refetchOnWindowFocus: false, // Không refetch khi focus vào window
    }
  );

  // Fetch detailed record for printing
  const { data: printDetailRecord, isLoading: isLoadingPrintDetail } = useQuery(
    ['stock-out-print-detail', printingRecordId],
    () => stockService.getStockOutVoucherDetail(printingRecordId!),
    {
      enabled: !!printingRecordId && printModalVisible,
      staleTime: 0,
      cacheTime: 0,
    }
  );


  const completeMutation = useMutation(
    (id: number) => stockService.completeStockOutVoucher(id),
    {
      onSuccess: () => {
        message.success('Hoàn thành xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
        queryClient.invalidateQueries('inventory');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi hoàn thành xuất kho');
      },
    }
  );

  const deleteMutation = useMutation(
    (id: number) => stockService.deleteStockOutVoucher(id),
    {
      onSuccess: () => {
        message.success('Xóa phiếu xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa phiếu xuất kho');
      },
    }
  );

  // Handlers
  const handleAdd = () => {
    navigate('/stock-out/new');
  };

  const handleEdit = (record: StockOutRecord) => {
    navigate(`/stock-out/edit/${record.id}`);
  };

  const handlePrint = async (record: StockOutRecord) => {
    setPrintingRecordId(record.id);
    await queryClient.removeQueries(['stock-out-print-detail', record.id]);
    setPrintModalVisible(true);
  };

  const handleComplete = async (id: number) => {
    try {
      // Kiểm tra items trước khi xác nhận
      const detailRecord = await stockService.getStockOutVoucherDetail(id);
      const items = detailRecord?.items || [];
      
      // Kiểm tra items có lot_id hay không
      const itemsWithoutLot = items.filter((item: StockOutItem) => 
        item.lot_id === null || item.lot_id === undefined || item.lot_id === 0 || item.lot_id === ''
      );
      
      if (itemsWithoutLot.length > 0) {
        const medicineNames = itemsWithoutLot.map(item => item.medicine_name || `ID: ${item.medicine_id}`).join(', ');
        message.error(`Không thể hoàn thành phiếu xuất kho. Các sản phẩm sau chưa có số lô: ${medicineNames}`);
        return;
      }
      
      Modal.confirm({
        title: 'Xác nhận hoàn thành xuất kho',
        content: 'Bạn có chắc chắn muốn hoàn thành phiếu xuất kho này? Hành động này sẽ cập nhật tồn kho.',
        okText: 'Hoàn thành',
        cancelText: 'Hủy',
        onOk: () => completeMutation.mutate(id),
      });
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi kiểm tra phiếu xuất kho');
    }
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận xóa phiếu xuất kho',
      content: 'Bạn có chắc chắn muốn xóa phiếu xuất kho này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handlePrintModalCancel = () => {
    setPrintModalVisible(false);
    setPrintingRecordId(null);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const clearFilters = () => {
    setFilters({ 
      status: '', 
      customer: '', 
      date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
      date_to: dayjs().endOf('month').format('YYYY-MM-DD')
    });
    setSearchText('');
  };

  // Statistics
  const totalRecords = stockOutRecords.length;
  const pendingRecords = stockOutRecords.filter((r: StockOutRecord) => r.status === 'pending').length;
  const completedRecords = stockOutRecords.filter((r: StockOutRecord) => r.status === 'completed').length;
  const totalAmount = stockOutRecords.reduce((sum: number, r: StockOutRecord) => sum + (r.total_amount || 0), 0);

  const getSummaryStats = () => {
    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <i className="fas fa-box-open" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng phiếu xuất"
                    value={totalRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <i className="fas fa-clock" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Chờ xử lý"
                    value={pendingRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Đã hoàn thành"
                    value={completedRecords}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#8b5cf6' }}>
                  <i className="fas fa-dollar-sign" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng giá trị"
                    value={totalAmount}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Filtered data
  const filteredRecords = stockOutRecords.filter((record: StockOutRecord) => {
    const matchesStatus = !filters.status || record.status === filters.status;
    
    // Tìm kiếm gần đúng theo mã phiếu hoặc tên khách hàng (không phân biệt hoa thường)
    const matchesSearch = !searchText || (() => {
      const searchLower = searchText.toLowerCase().trim();
      if (!searchLower) return true;
      
      // Loại bỏ dấu tiếng Việt để tìm kiếm gần đúng hơn
      const removeVietnameseAccents = (str: string) => {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      };
      
      const searchNormalized = removeVietnameseAccents(searchLower);
      
      // Tìm kiếm trong mã phiếu
      const voucherNumber = (record.reference_number || record.voucher_number || '').toLowerCase();
      const voucherNormalized = removeVietnameseAccents(voucherNumber);
      
      // Tìm kiếm trong tên khách hàng
      const customerName = (record.customer_name || '').toLowerCase();
      const customerNormalized = removeVietnameseAccents(customerName);
      
      return voucherNormalized.includes(searchNormalized) || 
             customerNormalized.includes(searchNormalized);
    })();
    
    return matchesStatus && matchesSearch;
  });

  // Cập nhật lại tổng số bản ghi khi filter thay đổi
  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, total: filteredRecords.length, current: 1 }));
  }, [filteredRecords.length, searchText, filters]);

  // Lấy dữ liệu trang hiện tại
  const pagedRecords = React.useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredRecords.slice(start, end);
  }, [filteredRecords, pagination]);

  const columns = [
    {
      title: 'Mã phiếu',
      dataIndex: 'reference_number',
      key: 'reference_number',
      width: 160,
      fixed: 'left' as const,
      render: (ref: string, record: StockOutRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937',
            lineHeight: '1.2'
          }}>
            {ref || record.voucher_number || 'N/A'}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            fontWeight: '400'
          }}>
            {record.created_at ? dayjs.utc(record.created_at).tz('Asia/Ho_Chi_Minh').format('DD/MM/YYYY HH:mm') : 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Khách hàng',
      key: 'customer',
      width: 200,
      render: (_: any, record: StockOutRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ 
            fontWeight: '500', 
            fontSize: '14px',
            color: '#1f2937',
            lineHeight: '1.3'
          }}>
            {record.customer_name || 'Khách lẻ'}
          </div>
          {record.customer_phone && (
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              fontWeight: '400'
            }}>
              {record.customer_phone}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Thông tin',
      key: 'info',
      width: 140,
      render: (_: any, record: StockOutRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
          <div style={{ 
            fontSize: '13px',
            color: '#374151',
            fontWeight: '500'
          }}>
            {record.total_items || 0} món
          </div>
          <div style={{ 
            fontSize: '14px',
            fontWeight: '600',
            color: '#059669'
          }}>
            {(record.total_amount || 0).toLocaleString('vi-VN')}đ
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig = {
          pending: { 
            color: '#f59e0b', 
            bgColor: '#fef3c7',
            text: 'Chờ xử lý'
          },
          completed: { 
            color: '#059669', 
            bgColor: '#d1fae5',
            text: 'Hoàn thành'
          },
          cancelled: { 
            color: '#dc2626', 
            bgColor: '#fee2e2',
            text: 'Đã hủy'
          }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <div style={{ 
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: config.bgColor,
            color: config.color,
            fontSize: '12px',
            fontWeight: '500',
            width: 'fit-content'
          }}>
            {config.text}
          </div>
        );
      },
      filters: [
        { text: 'Chờ xử lý', value: 'pending' },
        { text: 'Hoàn thành', value: 'completed' },
        { text: 'Đã hủy', value: 'cancelled' }
      ],
      onFilter: (value: any, record: StockOutRecord) => record.status === value,
    },
    {
      title: 'Người tạo phiếu',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 150,
      render: (name: string) => (
        <span style={{ 
          fontSize: '14px',
          color: '#374151',
          fontWeight: '500'
        }}>
          {name || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: StockOutRecord) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="In phiếu">
            <Button
              type="text"
              icon={<i className="fas fa-print" />}
              onClick={() => handlePrint(record)}
              size="small"
              style={{ 
                color: '#6b7280',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Sửa">
                <Button
                  type="text"
                  icon={<i className="fas fa-edit" />}
                  onClick={() => handleEdit(record)}
                  size="small"
                  style={{ 
                    color: '#f59e0b',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
              <Tooltip title="Hoàn thành">
                <Button
                  type="text"
                  icon={<i className="fas fa-check-circle" />}
                  onClick={() => handleComplete(record.id)}
                  size="small"
                  style={{ 
                    color: '#059669',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
              <Tooltip title="Xóa">
                <Button
                  type="text"
                  icon={<i className="fas fa-trash" />}
                  onClick={() => handleDelete(record.id)}
                  size="small"
                  style={{ 
                    color: '#dc2626',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                />
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  // Thiết lập locale cho dayjs
  dayjs.locale('vi');

  return (
    <ConfigProvider locale={locale}>
      <div className={styles.container}>
        {getSummaryStats()}

        <Card className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.searchSection}>
              <Search
                placeholder="Tìm kiếm theo mã phiếu, tên khách hàng (không phân biệt hoa thường)..."
                allowClear
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                size="large"
                style={{ textAlign: 'left' }}
              />
            </div>
            <div className={styles.filterSection}>
              <Space size="middle" wrap>
                <DatePicker
                  placeholder="Từ ngày"
                  value={filters.date_from ? dayjs(filters.date_from) : undefined}
                  onChange={(date) => setFilters(prev => ({ ...prev, date_from: date ? dayjs(date).format('YYYY-MM-DD') : undefined }))}
                  style={{ minWidth: 150 }}
                />
                <DatePicker
                  placeholder="Đến ngày"
                  value={filters.date_to ? dayjs(filters.date_to) : undefined}
                  onChange={(date) => setFilters(prev => ({ ...prev, date_to: date ? dayjs(date).format('YYYY-MM-DD') : undefined }))}
                  style={{ minWidth: 150 }}
                />
                <Select
                  placeholder="Lọc theo trạng thái"
                  allowClear
                  value={filters.status}
                  onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  style={{ minWidth: 150 }}
                >
                  <Option value="pending">Chờ xử lý</Option>
                  <Option value="completed">Hoàn thành</Option>
                  <Option value="cancelled">Đã hủy</Option>
                </Select>
                {(filters.status || filters.date_from || filters.date_to || searchText) && (
                  <Button
                    type="link"
                    icon={<FilterOutlined />}
                    onClick={clearFilters}
                    size="small"
                  >
                    Xóa bộ lọc
                  </Button>
                )}
              </Space>
            </div>
            <div className={styles.actionButtons}>
              <Space size="middle">
                <Tooltip title="Làm mới dữ liệu">
                  <Button
                    icon={<i className="fas fa-sync-alt" />}
                    onClick={() => window.location.reload()}
                    className={styles.actionButton}
                  >
                    Làm mới
                  </Button>
                </Tooltip>
                <Tooltip title="Tạo phiếu xuất kho mới">
                  <Button
                    type="primary"
                    icon={<i className="fas fa-plus" />}
                    onClick={handleAdd}
                    className={styles.primaryButton}
                  >
                    Tạo phiếu xuất
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={pagedRecords}
            rowKey="id"
            loading={isLoading}
            className={styles.stockOutTable}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} của ${total} phiếu xuất kho`,
              className: styles.tablePagination,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => setPagination(prev => ({ ...prev, current: page, pageSize }))
            }}
            size="middle"
            scroll={{ x: 800 }}
          />
        </Card>

      {/* Print Modal */}
      <StockOutPrintModal
        visible={printModalVisible}
        onCancel={handlePrintModalCancel}
        data={printDetailRecord || null}
        isLoading={isLoadingPrintDetail}
      />

    </div>
    </ConfigProvider>
  );
};

export default StockOutPage; 